import type { QuestionnaireFrequency } from '@gcv/shared';
import { useMutation } from '@tanstack/react-query';

import { QuestionnairesApi } from 'api';
import { isGenericResponse } from 'api/api-util/api-response';
import { BANK_QUESTIONNAIRE_KEY } from '../../keys';
import { useUser } from '../../user';
import { useBankQuestionnaireCache } from '../useBankQuestionnaireCache';

export interface UpdateQuestionnaireParams {
  questionnaireId: string;
  version: string;
  name: string;
  description: string;
  frequency: QuestionnaireFrequency;
  timeToRespond: number;
  reocurring: boolean;
}

export function useUpdateQuestionnaire() {
  const { userBankId } = useUser();
  const { applyQuestionnaireUpdate } = useBankQuestionnaireCache();
  const questionnairesApi = new QuestionnairesApi();

  const { mutateAsync: updateQuestionnaire, isPending: isUpdateQuestionnairePending } = useMutation({
    mutationKey: [BANK_QUESTIONNAIRE_KEY],
    mutationFn: ({
      questionnaireId,
      name,
      description,
      frequency,
      timeToRespond,
      reocurring
    }: UpdateQuestionnaireParams) =>
      questionnairesApi.updateQuestionnaire(userBankId, questionnaireId, {
        name,
        description,
        schedule: {
          reoccurring: reocurring,
          frequency,
          time_to_respond: timeToRespond,
          reminder: timeToRespond
        }
      }),
    onSuccess: (templates, { questionnaireId, version }) => {
      const updated = isGenericResponse(templates) ? undefined : templates.questionnaires?.[questionnaireId];

      if (updated) {
        applyQuestionnaireUpdate(updated, version);
      }
    },
    meta: {
      snackbarErrorMessage: 'There was an error editing this questionnaire.'
    }
  });

  return { updateQuestionnaire, isUpdateQuestionnairePending };
}
